import {
    EventLogActionType,
    EventLogDataType,
    EventLogWeekHours,
    EventLogWeeksHours,
} from '../../entities/EventLog/EventLog';
import trans from '../trans';

const isOldFormatData = (data: EventLogWeeksHours): data is {
    hours?: string;
    waitingDayHours?: string;
    leaveType?: string;
} => (
    typeof data === 'object'
    && data !== null
    && !Object.values(data).some((val) => typeof val === 'object' && val !== null && 'week' in val)
);

export const combineShift = (shift: any) => {
    const {
        plannedUsers,
        start,
        end,
        ...restShift
    } = shift;

    const combinedPlannedUsers: string = plannedUsers?.map((plannedUser: { fullName: string, id: string }) => plannedUser.fullName)
        .join(', ');

    const shiftPayload = {
        start,
        end,
        ...restShift,
        ...(combinedPlannedUsers ? { plannedUsers: combinedPlannedUsers } : {}),
    };

    return {
        [EventLogDataType.shift]: Object.fromEntries(
            Object.entries(shiftPayload).filter(([, v]) => v != null && v !== ''),
        ),
    };
};

export const combineTrackAndApproval = (track: any, approval: any) => {
    const {
        meal,
        pause,
        feedback,
        ...restTrack
    } = track;

    const combinedPause: string = [
        ...(pause ? [`Tijd: ${pause} minuten.`] : []),
        ...(meal ? [`Maaltijd: ${meal}.`] : []),
        ...(feedback ? [`Tevredenheid: ${feedback}.`] : []),
    ].filter(Boolean)
        .join(' ');

    return {
        [EventLogDataType.track]: {
            ...restTrack,
            ...(combinedPause ? { pauseMealFeedback: combinedPause } : {}),
        },
        [EventLogDataType.approval]: {
            ...(typeof approval === 'object' && approval.message ? { approval: approval.message } : {}),
        },
    };
};

export const combineApproval = (approval: any) => {
    const {
        user: approvalUser,
        start,
        end,
        uuid,
        message,
        ...restApproval
    } = approval;

    const combinedApproval: string = [
        ...(approvalUser ? [`Gebruiker: ${approvalUser}`] : []),
        ...(start ? [`Startdatum: ${start}`] : []),
        ...(end ? [`Einddatum: ${end}`] : []),
        ...(uuid ? [`UUID: ${uuid}`] : []),
    ].filter(Boolean)
        .join(', ');

    return {
        [EventLogDataType.approval]: {
            ...restApproval,
            ...(combinedApproval ? { approval: combinedApproval } : {}),
            ...(message ? { approval: message } : {}),
        },
    };
};

export const combineFeedback = (feedback: any) => {
    const {
        user: feedbackUser,
        rating,
        uuid,
        ...restFeedback
    } = feedback;

    const combinedFeedback: string = [
        ...(feedbackUser ? [`Gebruiker: ${feedbackUser}`] : []),
        ...(rating ? [`Feedback: ${rating}`] : []),
        ...(uuid ? [`UUID: ${uuid}`] : []),
    ].filter(Boolean)
        .join(', ');

    return {
        [EventLogDataType.feedback]: {
            ...restFeedback,
            feedback: combinedFeedback,
        },
    };
};

export const combineUser = (user: any) => {
    const {
        contractHoursPeriod,
        emergencyContact,
        loketEmployeeId,
        ...restUser
    } = user;

    const combinedContractHoursPeriod: string = Array.isArray(contractHoursPeriod) ? contractHoursPeriod.join(',\n') : contractHoursPeriod;

    return {
        [EventLogDataType.user]: {
            ...restUser,
            ...(combinedContractHoursPeriod ? { contractHoursPeriod: combinedContractHoursPeriod } : {}),
        },
    };
};

export const combineSettings = (setting: any) => {
    const settings = Object.entries(setting);

    const transformedSettings = settings.map(([key, value]) => {
        const values = Object.entries(value as object);
        const combinedValue = values.map(([valueKey, valueValue]) => `${trans(`entities.eventLog.categoryItem.${valueKey}`)}: ${valueValue}`)
            .join(', ');

        return [key, combinedValue];
    });

    return {
        [EventLogDataType.setting]: {
            ...Object.fromEntries(transformedSettings),
        },
    };
};

export const formatHoursBlock = (hoursData: EventLogWeeksHours) => {
    if (isOldFormatData(hoursData)) {
        return {
            ...(hoursData.hours ? { hours: hoursData.hours } : {}),
            ...(hoursData.waitingDayHours ? { waitingDayHours: hoursData.waitingDayHours } : {}),
            ...(hoursData.leaveType ? { leaveType: hoursData.leaveType } : {}),
        };
    }

    const weeks = Object.values(hoursData);

    const formatByKey = (key: Exclude<keyof EventLogWeekHours, 'week'>) => weeks
        .filter((week) => typeof week === 'object' && week !== null && key in week)
        .map((week) => `week ${(week as EventLogWeekHours).week}: ${(week as EventLogWeekHours)[key]}`)
        .join('\n');

    return Object.fromEntries(Object.entries({
        hours: formatByKey('hours'),
        waitingDayHours: formatByKey('waitingDayHours'),
        leaveType: formatByKey('leaveType'),
    }).filter(([, value]) => value));
};

export const handleCreatedUser = (user: any, action: EventLogActionType, id: string, type: any) => {
    const isCreatedUser = (action === EventLogActionType.created && typeof user === 'object')
        && user !== null
        && 'meta' in user
        && typeof user.meta === 'object';

    if (isCreatedUser) {
        const fullName = user?.fullName ?? trans('entities.eventLog.nameUnknown');

        const data = {
            [EventLogDataType.person]: {
                person: trans('entities.eventLog.createdNewUser', { name: fullName }),
            },
        };

        return {
            id,
            data,
            type,
        };
    }

    return null;
};

export const handleDeletedShift = (shift: any, action: EventLogActionType, id: string, type: any, body: any) => {
    const isDeletedShift = (action === EventLogActionType.deleted && typeof shift === 'object')
        && shift !== null
        && 'meta' in shift
        && typeof shift.meta === 'object';

    if (isDeletedShift) {
        const time = `${shift?.start} - ${shift?.end}`;

        const data: { [key: string]: any } = {
            [EventLogDataType.shift]: {
                shift: trans('entities.eventLog.deletedShift', { time }),
            },
        };

        if ('ShiftPlanning' in body) {
            const shiftPlanningValues = Object.values(body.ShiftPlanning);

            shiftPlanningValues.forEach((value: any, index) => {
                const fullName = value?.user?.fullName;

                if (fullName) {
                    data[`Person${index + 1}`] = {
                        person: fullName,
                    };
                } else {
                    console.error('No full name available');
                }
            });
        }

        return {
            id,
            data,
            type,
        };
    }

    return null;
};

export const formatEmergencyContact = (emergencyContact: any) => {
    const {
        fullName,
        phone,
        relationType,
        ...restEmergencyContact
    } = emergencyContact;

    return {
        [EventLogDataType.emergencyContact]: {
            ...restEmergencyContact,
            ...(fullName && { emergencyContactFullName: fullName }),
            ...(phone && { emergencyContactPhone: phone }),
            ...(relationType && { emergencyContactRelationType: relationType }),
        },
    };
};
